import { FC } from "react"
import { AutoComplete, Button, Card, Col, Form, Input, InputNumber, Popconfirm, Row, Select } from "antd"
import { FormListFieldData } from "antd/es/form"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"

import { GameSelect } from "components"
import { communityEventMetrics } from "domains/managingJson/store/types"
import {
  COMMUNITY_EVENT_GOAL_ID_PATTERN,
  COMMUNITY_EVENT_MAX_SOURCES,
  COMMUNITY_EVENT_REWARD_TYPES,
  toOptions,
} from "./constants"
import { COMMUNITY_EVENT_DEFAULT_MIN_CONTRIBUTION } from "./defaults"
import {
  metricsValidator,
  milestonesValidator,
  minContributionValidator,
  percentValidator,
  positiveNumberValidator,
  sourcesValidator,
  targetCoversMinContributionValidator,
  uniqueGoalId,
  uniqueMilestonePercent,
  uniqueSourceGame,
} from "./validators"
import s from "./styles.module.css"

const MIN_CONTRIBUTION_HINT = "A player's own count for this goal must reach this before they qualify for its rewards. "
  + `Empty means ${COMMUNITY_EVENT_DEFAULT_MIN_CONTRIBUTION}.`

export const GoalFields: FC<{
  name: number;
  index: number;
  goalFields: FormListFieldData[];
  onRemove: () => void;
}> = ({ name, index, goalFields, onRemove }) => {
  const goalId: string | undefined = Form.useWatch(["goals", name, "goalId"])
  const goalIdUnique = uniqueGoalId(["goals"])
  const sourceGameUnique = uniqueSourceGame(["goals", name, "sources"])
  const milestonePercentUnique = uniqueMilestonePercent(["goals", name, "milestones"])

  return (
    <Card
      size="small"
      title={goalId ? `Goal ${index + 1} · ${goalId}` : `Goal ${index + 1}`}
      extra={
        <Popconfirm
          title="Remove this goal with its games and milestones?"
          disabled={goalFields.length <= 1}
          onConfirm={onRemove}
          okText="Remove"
          okType="danger"
        >
          <Button icon={<DeleteOutlined />} danger disabled={goalFields.length <= 1} />
        </Popconfirm>
      }
      className={s.goalCard}
    >
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Goal id"
            name={[name, "goalId"]}
            dependencies={goalIdUnique.dependencies(goalFields)}
            rules={[
              { required: true, message: "Please input the goal id" },
              { pattern: COMMUNITY_EVENT_GOAL_ID_PATTERN, message: "Lowercase letters, digits and hyphens only, up to 32" },
              goalIdUnique.validator,
            ]}
          >
            <Input placeholder="great-pumpkin" />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item label="Title" name={[name, "title"]} extra="Empty shows the theme label">
            <Input />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Target"
            name={[name, "target"]}
            dependencies={[["goals", name, "minContribution"]]}
            rules={[
              { required: true, message: "Input the target" },
              positiveNumberValidator,
              targetCoversMinContributionValidator(["goals", name]),
            ]}
          >
            <InputNumber className={s.fullWidth} />
          </Form.Item>
        </Col>
        <Col span={16}>
          <Form.Item
            label="Minimum contribution to qualify"
            name={[name, "minContribution"]}
            rules={[minContributionValidator]}
            extra={MIN_CONTRIBUTION_HINT}
          >
            <InputNumber placeholder={String(COMMUNITY_EVENT_DEFAULT_MIN_CONTRIBUTION)} className={s.fullWidth} />
          </Form.Item>
        </Col>
      </Row>

      <div className={s.sectionTitle}>Games and stats</div>
      <Form.List name={[name, "sources"]} rules={[sourcesValidator]}>
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name: sourceName }) => (
              <Row key={key} gutter={16}>
                <Col span={10}>
                  <Form.Item
                    name={[sourceName, "gameId"]}
                    dependencies={sourceGameUnique.dependencies(fields)}
                    rules={[{ required: true, message: "Select a game" }, sourceGameUnique.validator]}
                  >
                    <GameSelect style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name={[sourceName, "metrics"]} rules={[metricsValidator]}>
                    <Select mode="multiple" placeholder="Stats that count" options={toOptions(communityEventMetrics)} />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Button icon={<DeleteOutlined />} danger disabled={fields.length <= 1} onClick={() => remove(sourceName)} />
                </Col>
              </Row>
            ))}
            <Button
              type="dashed"
              disabled={fields.length >= COMMUNITY_EVENT_MAX_SOURCES}
              onClick={() => add()}
              icon={<PlusOutlined />}
            >
              Add game
            </Button>
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>

      <div className={s.sectionTitle}>Milestones</div>
      <Form.List name={[name, "milestones"]} rules={[milestonesValidator]}>
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name: milestoneName }) => (
              <Row key={key} gutter={16}>
                <Col span={6}>
                  <Form.Item
                    name={[milestoneName, "pct"]}
                    dependencies={milestonePercentUnique.dependencies(fields)}
                    rules={[{ required: true, message: "Input the percent" }, percentValidator, milestonePercentUnique.validator]}
                  >
                    <InputNumber placeholder="Percent" addonAfter="%" className={s.fullWidth} />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={[milestoneName, "reward", "type"]}
                    rules={[{ required: true, whitespace: true, message: "Input the reward type" }]}
                  >
                    <AutoComplete options={toOptions(COMMUNITY_EVENT_REWARD_TYPES)} placeholder="Reward type" />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={[milestoneName, "reward", "amount"]}
                    rules={[{ required: true, message: "Input the amount" }, positiveNumberValidator]}
                  >
                    <InputNumber placeholder="Amount" className={s.fullWidth} />
                  </Form.Item>
                </Col>
                <Col span={2}>
                  <Button icon={<DeleteOutlined />} danger onClick={() => remove(milestoneName)} />
                </Col>
              </Row>
            ))}
            <Button type="dashed" onClick={() => add()} icon={<PlusOutlined />}>Add milestone</Button>
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>
    </Card>
  )
}
